import { ref } from 'vue'

const BOOT_SPLASH_DISMISS_MS = 420

/** Reactive flag for idle chrome (screensaver, etc.) while `#app-loading` covers the app. */
export const bootSplashVisible = ref(
  typeof document !== 'undefined' && document.getElementById('app-loading') != null
)

/** Clears the inline boot watchdog from index.html once the app shell is up (splash may stay visible). */
export function markBootSequenceReady(): void {
  window.__lucimarkBootComplete?.()
}

export function isBootSplashPresent(): boolean {
  return document.getElementById('app-loading') != null
}

/** Fade out and remove `#app-loading` (no-op when already gone). */
export function dismissBootSplash(): void {
  const el = document.getElementById('app-loading')
  if (el == null) {
    bootSplashVisible.value = false
    return
  }
  markBootSequenceReady()

  const remove = () => {
    bootSplashVisible.value = false
    el.remove()
  }

  const prefersReducedMotion
    = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (prefersReducedMotion) {
    remove()
    return
  }

  el.classList.add('app-loading--dismissing')
  const onEnd = (ev: TransitionEvent) => {
    if (ev.target !== el || ev.propertyName !== 'opacity') {
      return
    }
    remove()
  }
  el.addEventListener('transitionend', onEnd)
  window.setTimeout(remove, BOOT_SPLASH_DISMISS_MS + 80)
}

/** Legacy: clear watchdog and remove splash immediately (failure paths). */
export function completeBootSplash(): void {
  dismissBootSplash()
}

/** Wait for two animation frames so Vue can paint under the boot splash. */
export function waitForAppPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
  })
}

/** Surfaces the inline boot error UI (cache / SW skew, init failure). */
export function showBootSplashFailure(message?: string): void {
  window.__lucimarkShowBootFailure?.(message)
}
